/**
 * nav.ts — The app-shell navigation model.
 *
 * Mirrors the CME Studio prototype's `shell.jsx` Sidebar `sections` array
 * (cme/project/app, bundle 8g2bTVkJuAkJdrsriC0NeQ). Each stage maps to a
 * route; the sidebar, breadcrumb, and AI-assistant context all derive from
 * this single source of truth.
 */
import { I, type IconComponent } from "../components/icons";

export type StageId = "ingest" | "audit" | "strategy" | "kb" | "kb-explore" | "suppliers";

export type SectionLabel = "PROGETTO" | "ARCHIVIO";

export interface NavItem {
  id: StageId;
  /** Sidebar label (Italian, matching the prototype copy). */
  label: string;
  /** Router path. */
  path: string;
  icon: IconComponent;
  section: SectionLabel;
  /** AI-assistant context key (drives suggested prompts + the context label). */
  aiContext: string;
  /** When true, the right-rail AI assistant is hidden on this stage
   *  (Esplora KB has its own integrated chat panel — see CD-4/CD-5). */
  hideAI?: boolean;
  /** Section gated behind an "In arrivo" screen (no backend domain yet). */
  comingSoon?: boolean;
}

export const NAV_ITEMS: NavItem[] = [
  {
    id: "ingest",
    label: "Workspace",
    path: "/",
    icon: I.Upload,
    section: "PROGETTO",
    aiContext: "ingest",
  },
  {
    id: "audit",
    label: "Audit & Discrepanze",
    path: "/audit",
    icon: I.Audit,
    section: "PROGETTO",
    aiContext: "audit",
  },
  {
    id: "strategy",
    label: "Strategia & Margini",
    path: "/strategy",
    icon: I.Strategy,
    section: "PROGETTO",
    aiContext: "strategy",
  },
  {
    id: "kb",
    label: "Knowledge Base",
    path: "/kb",
    icon: I.Layers,
    section: "ARCHIVIO",
    aiContext: "kb",
  },
  {
    id: "kb-explore",
    label: "Esplora KB",
    path: "/kb/explore",
    icon: I.Network,
    section: "ARCHIVIO",
    aiContext: "kb",
    hideAI: true,
  },
  {
    id: "suppliers",
    label: "Fornitori & Storico",
    path: "/suppliers",
    icon: I.Suppliers,
    section: "PROGETTO",
    aiContext: "suppliers",
  },
];

export const SECTIONS: { label: SectionLabel; items: NavItem[] }[] = [
  {
    label: "PROGETTO",
    items: NAV_ITEMS.filter((i) => i.section === "PROGETTO" && i.id !== "suppliers"),
  },
  {
    label: "ARCHIVIO",
    items: [
      ...NAV_ITEMS.filter((i) => i.id === "kb" || i.id === "kb-explore"),
      ...NAV_ITEMS.filter((i) => i.id === "suppliers"),
    ],
  },
];

/** Resolve the active nav item from a router pathname. */
export function stageFromPath(pathname: string): NavItem {
  // Most specific first ("/kb/explore" before "/kb"); "/" matches exactly.
  const byLength = [...NAV_ITEMS].sort((a, b) => b.path.length - a.path.length);
  const match = byLength.find((i) =>
    i.path === "/" ? pathname === "/" : pathname.startsWith(i.path),
  );
  return match ?? NAV_ITEMS[0];
}

/** Italian label for the AI-assistant context line. */
export function contextLabel(context: string): string {
  return (
    {
      ingest: "Workspace",
      audit: "Audit",
      strategy: "Strategia",
      suppliers: "Fornitori",
      kb: "Knowledge Base",
    }[context] ?? "Progetto"
  );
}
